/**
 * space_ew_client.ts — the client half of THE ONE EW EFFECT (space campaign slice I, 2026-09-30; report §21.10).
 *
 * The server owns the effect (`ew_effect`, row-filtered to its source and target) and every rule that matters: the
 * legal target, the fit, the range, the cooldowns, the breaks, the no-stack rule and the 2/3 meter on the target's
 * survey work. This file subscribes to the rows THIS pilot may see, picks the jammer's target the way a pilot aims (the
 * warring pilot nearest the nose, ≤ 600 m, in a 25° cone, not behind a solid the client knows), sends the requests with
 * fresh request ids (a retry of the SAME id never spends twice), and builds the HUD's words. The capacitor (10 for the
 * jam, 15 for the burst) is debited by the caller on the server's ACCEPTANCE.
 */
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { keyHint } from './keybinds.js';
import {
  EW_RANGE_M, EW_BREAK_RANGE_M, EW_END_TEXT, EW_CAP_COST, ECCM_CAP_COST, losClear, type EwEndReason, type EwSolid,
} from '@shared/space_ew.js';

type Conn = NonNullable<typeof atlas.conn>;
type Db = Conn['db'];
export type EffectRow = ReturnType<Db['ewEffect']['effectId']['find']> & object;
export type EwPilotRow = ReturnType<Db['ewPilot']['identity']['find']> & object;

export const EW_SUBS = ['SELECT * FROM ew_effect', 'SELECT * FROM ew_pilot'];
/** The jammer aims like a gun: the target sits within this cone of the nose. */
export const JAM_CONE_RAD = 25 * Math.PI / 180;

const msOf = (t: { microsSinceUnixEpoch: bigint }): number => Number(t.microsSinceUnixEpoch / 1000n);
interface P3 { x: number; y: number; z: number }

/** Pick the jammer's target: the warring pilot nearest the nose inside the cone and range, in sight. PURE. */
export function pickJamTarget(
  me: P3, fwd: P3, cands: ReadonlyArray<{ hex: string; pos: P3 }>, solids: readonly EwSolid[] = [],
): { hex: string; rangeM: number } | { hex: null; why: string } {
  let best: { hex: string; rangeM: number; ang: number } | null = null;
  let nearest = Infinity, blocked = false;
  const fl = Math.hypot(fwd.x, fwd.y, fwd.z) || 1;
  for (const c of cands) {
    const dx = c.pos.x - me.x, dy = c.pos.y - me.y, dz = c.pos.z - me.z;
    const d = Math.hypot(dx, dy, dz);
    if (!Number.isFinite(d)) continue;
    nearest = Math.min(nearest, d);
    if (d > EW_RANGE_M || d < 1e-6) continue;
    const ang = Math.acos(Math.max(-1, Math.min(1, (dx * fwd.x + dy * fwd.y + dz * fwd.z) / (d * fl))));
    if (ang > JAM_CONE_RAD) continue;
    if (!losClear(me, c.pos, solids)) { blocked = true; continue; }
    if (!best || ang < best.ang) best = { hex: c.hex, rangeM: d, ang };
  }
  if (best) return { hex: best.hex, rangeM: best.rangeM };
  if (!cands.length) return { hex: null, why: 'No pilot your guild is at war with is in this sky — the jammer needs a live wardec' };
  if (blocked) return { hex: null, why: 'Something solid stands between you and the warring pilot ahead' };
  if (nearest > EW_RANGE_M) return { hex: null, why: `The nearest warring pilot is ${Math.round(nearest)} m out — close to ${EW_RANGE_M} m` };
  return { hex: null, why: `Point your nose at the warring pilot (within ${Math.round(JAM_CONE_RAD * 180 / Math.PI)}°)` };
}

export interface EwHudFacts {
  nowMs: number;
  nameOf: (hex: string) => string;
  sepTo: (hex: string) => number | null;  // my live separation to that pilot (m), when the client sees them
  surveying: boolean;                      // my metered beam work is running
}
export interface EwHudView {
  tone: 'jammed' | 'jamming' | 'hardened' | 'ended' | 'none';
  lines: string[];
  /** One short line for a short screen (720p at 200 % UI scale): the same facts, fewer words. */
  compact: string;
  jammerHex: string | null;                // the pilot jamming ME (the HUD brackets them)
  jammedByMe: string | null;
}

class EwClient {
  private subConn: Conn | null = null;
  private rowSub: { unsubscribe(): void } | null = null;
  private rowApplied = false;
  private rowKey = '';
  private readonly nonce = Math.random().toString(36).slice(2, 8);
  private seq = 0;
  private busy = { jam: false, burst: false };
  readonly stats = { jamsSent: 0, jamsOk: 0, burstsSent: 0, burstsOk: 0, refused: 0, lastError: '', lastOk: '' };

  get conn(): Conn | null { return atlas.conn ?? null; }
  /** Subscribe once per connection (idempotent — called from the HUD tick). */
  ensure(): void {
    const c = this.conn;
    if (!c || this.subConn === c) return;
    this.subConn = c;
    try { c.subscriptionBuilder().subscribe(EW_SUBS); } catch { /* pre-publish */ }
  }
  private me(): Identity | null { return this.conn?.identity ?? null; }

  /**
   * THE AOI GAP (space campaign I): the AOI window (±3 cells of 64 m) holds pilots within ~190 m, so a jammer at 400 m
   * is no remote at all — no hull drawn, no contact. THE SQUAD LAW's pattern (slice B): the pilots EW needs ride their
   * own `player_state` rows — who jams me, whom I jam, and the warring pilots the jammer may aim at (≤ 32), released
   * when the list changes (THE AOI GATE keeps them from becoming avatars). Subscribe-new-then-unsubscribe-old.
   */
  watch(hexes: readonly string[]): void {
    const c = this.conn;
    if (!c) return;
    const list = [...new Set(hexes.map((h) => h.replace(/^0x/, '')))].slice(0, 32).sort();
    const key = list.join(',');
    if (key === this.rowKey && this.subConn === c) return;
    this.rowKey = key;
    const old = this.rowSub, oldApplied = this.rowApplied;
    this.rowSub = null; this.rowApplied = false;
    if (list.length) {
      try {
        const h = c.subscriptionBuilder().onApplied(() => { if (this.rowSub === h) this.rowApplied = true; else { try { h.unsubscribe(); } catch { /* gone */ } } })
          .subscribe(list.map((hx) => `SELECT * FROM player_state WHERE identity = 0x${hx}`)) as unknown as { unsubscribe(): void };
        this.rowSub = h;
      } catch { /* pre-publish */ }
    }
    if (old && oldApplied) { try { old.unsubscribe(); } catch { /* gone */ } }
  }
  /** A watched (or AOI) pilot's server row: absolute position, system, mode. */
  rowOf(hex: string): { x: number; y: number; z: number; systemId: string; mode: string } | null {
    const c = this.conn;
    if (!c) return null;
    try {
      const id = new Identity(hex.replace(/^0x/, ''));
      const p = c.db.playerState.identity.find(id);
      if (!p) return null;
      const l = c.db.spaceLocation.identity.find(id);
      return { x: p.posX, y: p.posY, z: p.posZ, systemId: l?.systemId ?? 'sys0', mode: l?.mode ?? 'flight' };
    } catch { return null; }
  }
  /** The pilots EW is about right now: who jams me, whom I jam. */
  parties(nowMs: number): string[] {
    const out: string[] = [];
    for (const e of this.onMe(nowMs)) out.push(e.source.toHexString());
    const m = this.mine(nowMs);
    if (m) out.push(m.target.toHexString());
    return out;
  }
  private rows(): EffectRow[] { try { return [...(this.conn?.db.ewEffect.iter() ?? [])] as EffectRow[]; } catch { return []; } }
  pilot(): EwPilotRow | null {
    const c = this.conn, me = this.me();
    if (!c || !me) return null;
    try { return (c.db.ewPilot.identity.find(me) as EwPilotRow | undefined) ?? null; } catch { return null; }
  }
  /** Live effects ON me (state active, before expiry). */
  onMe(nowMs: number): EffectRow[] {
    const me = this.me();
    return me ? this.rows().filter((e) => e.target.isEqual(me) && e.state === 'active' && msOf(e.expiresAt) > nowMs) : [];
  }
  /** My live effect on someone. */
  mine(nowMs: number): EffectRow | null {
    const me = this.me();
    return me ? this.rows().find((e) => e.source.isEqual(me) && e.state === 'active' && msOf(e.expiresAt) > nowMs) ?? null : null;
  }
  /** The most recent effect that ended within `withinMs` (either side) — the HUD's "why it ended". */
  lastEnded(nowMs: number, withinMs = 4000): EffectRow | null {
    let best: EffectRow | null = null;
    for (const e of this.rows()) {
      if (e.state !== 'ended' || nowMs - msOf(e.endedAt) > withinMs) continue;
      if (!best || msOf(e.endedAt) > msOf(best.endedAt)) best = e;
    }
    return best;
  }
  jamReadyInMs(nowMs: number): number { const p = this.pilot(); return p ? Math.max(0, msOf(p.jamReadyAt) - nowMs) : 0; }
  burstReadyInMs(nowMs: number): number { const p = this.pilot(); return p ? Math.max(0, msOf(p.burstReadyAt) - nowMs) : 0; }
  resistInMs(nowMs: number): number { const p = this.pilot(); return p ? Math.max(0, msOf(p.resistUntil) - nowMs) : 0; }
  private nextReq(kind: string): string { this.seq++; return `${this.nonce}-${kind}-${this.seq}`; }

  /** Request a jam on `hex`. `done(ok, msg)` fires once with the server's answer. */
  jam(hex: string, done: (ok: boolean, msg: string) => void): void {
    const c = this.conn;
    if (!c) { done(false, 'Not connected'); return; }
    if (this.busy.jam) return;
    this.busy.jam = true;
    this.stats.jamsSent++;
    const reducers = c.reducers as unknown as { ewJam(a: { target: Identity; requestId: string }): Promise<void> };
    reducers.ewJam({ target: new Identity(hex), requestId: this.nextReq('j') })
      .then(() => { this.busy.jam = false; this.stats.jamsOk++; this.stats.lastOk = `jam ${hex.slice(0, 8)}`; done(true, ''); })
      .catch((e: Error) => { this.busy.jam = false; this.stats.refused++; this.stats.lastError = String(e?.message ?? e); done(false, this.stats.lastError); });
  }
  /** Fire the sensor burst. */
  burst(done: (ok: boolean, msg: string) => void): void {
    const c = this.conn;
    if (!c) { done(false, 'Not connected'); return; }
    if (this.busy.burst) return;
    this.busy.burst = true;
    this.stats.burstsSent++;
    const reducers = c.reducers as unknown as { ewBurst(a: { requestId: string }): Promise<void> };
    reducers.ewBurst({ requestId: this.nextReq('b') })
      .then(() => { this.busy.burst = false; this.stats.burstsOk++; this.stats.lastOk = 'burst'; done(true, ''); })
      .catch((e: Error) => { this.busy.burst = false; this.stats.refused++; this.stats.lastError = String(e?.message ?? e); done(false, this.stats.lastError); });
  }

  /** The HUD's words (text + glyph, never colour alone) — the strip under the reticle. */
  hud(f: EwHudFacts): EwHudView {
    const on = this.onMe(f.nowMs);
    const s = (ms: number): string => `${(Math.max(0, ms) / 1000).toFixed(1)} s`;
    if (on.length) {
      const e = on[0];
      const hex = e.source.toHexString();
      const sep = f.sepTo(hex);
      const burstIn = this.burstReadyInMs(f.nowMs);
      return {
        tone: 'jammed', jammerHex: hex, jammedByMe: null,
        compact: `≋ JAMMED by ${f.nameOf(hex)} ${s(msOf(e.expiresAt) - f.nowMs)} · ${keyHint('spaceCountermeasures')} burst${burstIn > 0 ? ` ${s(burstIn)}` : ''} · >${EW_BREAK_RANGE_M} m · shoot ◇`,
        lines: [
          `≋ SENSORS JAMMED by ${f.nameOf(hex)} — ${s(msOf(e.expiresAt) - f.nowMs)} left${sep !== null ? ` · ◇ ${Math.round(sep)} m` : ''}${f.surveying ? ' · survey work at ⅔' : ''} · lead delayed`,
          `Counter: ${keyHint('spaceCountermeasures')} sensor burst (${burstIn > 0 ? `recharging ${s(burstIn)}` : `${ECCM_CAP_COST} cap, ready`}) · fly past ${EW_BREAK_RANGE_M} m · shoot the ◇ jammer`,
        ],
      };
    }
    const mine = this.mine(f.nowMs);
    if (mine) {
      const hex = mine.target.toHexString();
      const sep = f.sepTo(hex);
      return {
        tone: 'jamming', jammerHex: null, jammedByMe: hex,
        compact: `≋ JAMMING ${f.nameOf(hex)} ${s(msOf(mine.expiresAt) - f.nowMs)}`,
        lines: [`≋ JAMMING ${f.nameOf(hex)} — ${s(msOf(mine.expiresAt) - f.nowMs)} left${sep !== null ? ` · ${Math.round(sep)} m (breaks past ${EW_BREAK_RANGE_M} m)` : ''} · the jammer cycles ${s(this.jamReadyInMs(f.nowMs))}`],
      };
    }
    const resist = this.resistInMs(f.nowMs);
    if (resist > 0) return { tone: 'hardened', jammerHex: null, jammedByMe: null, compact: `◈ HARDENED ${s(resist)}`, lines: [`◈ SENSORS HARDENED — ${s(resist)} (no interference lands)`] };
    const ended = this.lastEnded(f.nowMs);
    if (ended) {
      const me = this.me();
      const mineSide = !!me && ended.source.isEqual(me);
      const why = EW_END_TEXT[ended.endReason as EwEndReason] ?? ended.endReason;
      return { tone: 'ended', jammerHex: null, jammedByMe: null, compact: `≋ ended — ${why}`, lines: [`≋ ${mineSide ? 'Your interference' : 'The interference'} ended — ${why}`] };
    }
    return { tone: 'none', jammerHex: null, jammedByMe: null, compact: '', lines: [] };
  }
}

/** The capacitor costs, for the caller's debit on acceptance. */
export const EW_COSTS = { jam: EW_CAP_COST, burst: ECCM_CAP_COST } as const;
export const ew = new EwClient();
